import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSiteContent } from "@/contexts/SiteContentContext";
import AdminShell from "@/components/admin/AdminShell";
import LocalizedInput from "@/components/admin/LocalizedInput";
import ImageUploader from "@/components/admin/ImageUploader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Loader2, Save, RotateCcw, Plus, Trash2 } from "lucide-react";

const EMPTY_LOC = { de: "", en: "", es: "" };

const SectionCard = ({ title, children }) => (
  <section className="rounded-3xl bg-white border border-neutral-200 p-6 space-y-5">
    <h2 className="font-display text-xl font-medium text-neutral-950">{title}</h2>
    {children}
  </section>
);

const AdminSite = () => {
  const { refresh } = useSiteContent();
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/site/content");
      setDoc(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site/content", doc);
      toast.success("Homepage gespeichert");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Inhalt der Homepage komplett auf Standard zurücksetzen?")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/site/reset");
      setDoc(data);
      toast.success("Auf Standard zurückgesetzt");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!doc) {
    return (
      <AdminShell title="Homepage bearbeiten">
        <div className="py-24 grid place-items-center">
          <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
        </div>
      </AdminShell>
    );
  }

  const updateField = (path, value) => {
    setDoc((d) => {
      const next = JSON.parse(JSON.stringify(d));
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const updateArrayItem = (key, idx, value) => {
    setDoc((d) => {
      const next = JSON.parse(JSON.stringify(d));
      next[key] = next[key] || [];
      next[key][idx] = { ...next[key][idx], ...value };
      return next;
    });
  };

  const removeArrayItem = (key, idx) => {
    setDoc((d) => {
      const next = JSON.parse(JSON.stringify(d));
      next[key] = (next[key] || []).filter((_, i) => i !== idx);
      return next;
    });
  };

  const addTestimonial = () => {
    setDoc((d) => ({
      ...d,
      testimonials: [
        ...(d.testimonials || []),
        { name: "", role: { ...EMPTY_LOC }, text: { ...EMPTY_LOC } },
      ],
    }));
  };

  return (
    <AdminShell
      title="Homepage bearbeiten"
      subtitle="Texte, Bilder und Daten aller Hauptbereiche der Website."
      actions={
        <>
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm hover:bg-neutral-50"
            data-testid="admin-site-reset"
          >
            <RotateCcw className="w-4 h-4" />
            Zurücksetzen
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-4 py-2 text-sm font-medium hover:bg-black disabled:opacity-60"
            data-testid="admin-site-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Speichern
          </button>
        </>
      }
    >
      <Tabs defaultValue="hero" className="space-y-6">
        <TabsList className="bg-white rounded-full p-1 border border-neutral-200">
          <TabsTrigger value="hero" data-testid="site-tab-hero">Hero</TabsTrigger>
          <TabsTrigger value="trust" data-testid="site-tab-trust">Trust</TabsTrigger>
          <TabsTrigger value="services" data-testid="site-tab-services">Services</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="site-tab-testimonials">Testimonials</TabsTrigger>
          <TabsTrigger value="contact" data-testid="site-tab-contact">Kontakt</TabsTrigger>
        </TabsList>

        {/* HERO */}
        <TabsContent value="hero" className="space-y-6">
          <SectionCard title="Hero-Bereich">
            <ImageUploader
              value={doc.hero?.background_url || ""}
              onChange={(url) => updateField("hero.background_url", url)}
              label="Hintergrundbild"
              testId="hero-bg"
            />
            <LocalizedInput
              label="Eyebrow (Mini-Überschrift)"
              value={doc.hero?.eyebrow || EMPTY_LOC}
              onChange={(v) => updateField("hero.eyebrow", v)}
              testId="hero-eyebrow"
            />
            <LocalizedInput
              label="Haupttitel"
              value={doc.hero?.title || EMPTY_LOC}
              onChange={(v) => updateField("hero.title", v)}
              multi
              rows={3}
              testId="hero-title"
              required
            />
            <LocalizedInput
              label="Untertitel"
              value={doc.hero?.subtitle || EMPTY_LOC}
              onChange={(v) => updateField("hero.subtitle", v)}
              multi
              testId="hero-subtitle"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LocalizedInput
                label="Button 1 (primär)"
                value={doc.hero?.cta_primary || EMPTY_LOC}
                onChange={(v) => updateField("hero.cta_primary", v)}
                testId="hero-cta1"
              />
              <LocalizedInput
                label="Button 2 (WhatsApp)"
                value={doc.hero?.cta_whatsapp || EMPTY_LOC}
                onChange={(v) => updateField("hero.cta_whatsapp", v)}
                testId="hero-cta2"
              />
            </div>
          </SectionCard>
        </TabsContent>

        {/* TRUST */}
        <TabsContent value="trust" className="space-y-6">
          <SectionCard title="Trust-Bereich">
            <LocalizedInput
              label="Headline"
              value={doc.trust?.headline || EMPTY_LOC}
              onChange={(v) => updateField("trust.headline", v)}
              testId="trust-headline"
            />

            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
                Badges
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(doc.trust?.badges || []).map((b, i) => (
                  <div key={i} className="rounded-2xl border border-neutral-200 p-4">
                    <LocalizedInput
                      label={`Badge ${i + 1}`}
                      value={b.label || EMPTY_LOC}
                      onChange={(v) => {
                        const arr = [...(doc.trust?.badges || [])];
                        arr[i] = { ...arr[i], label: v };
                        updateField("trust.badges", arr);
                      }}
                      testId={`trust-badge-${i}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">
                KPIs
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(doc.trust?.kpis || []).map((k, i) => (
                  <div key={i} className="rounded-2xl border border-neutral-200 p-4 space-y-3">
                    <div className="space-y-1">
                      <div className="text-[10px] uppercase tracking-wider text-neutral-400">
                        Wert (z.B. 400 Mbit/s)
                      </div>
                      <Input
                        value={k.value || ""}
                        onChange={(e) => {
                          const arr = [...(doc.trust?.kpis || [])];
                          arr[i] = { ...arr[i], value: e.target.value };
                          updateField("trust.kpis", arr);
                        }}
                        data-testid={`trust-kpi-${i}-value`}
                        className="rounded-xl"
                      />
                    </div>
                    <LocalizedInput
                      label="Label"
                      value={k.label || EMPTY_LOC}
                      onChange={(v) => {
                        const arr = [...(doc.trust?.kpis || [])];
                        arr[i] = { ...arr[i], label: v };
                        updateField("trust.kpis", arr);
                      }}
                      testId={`trust-kpi-${i}-label`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>
        </TabsContent>

        {/* SERVICES */}
        <TabsContent value="services" className="space-y-4">
          {(doc.services || []).map((s, i) => (
            <SectionCard key={s.key || i} title={`Service ${i + 1} · ${s.key || ""}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-400">Key</div>
                  <Input
                    value={s.key || ""}
                    onChange={(e) => updateArrayItem("services", i, { key: e.target.value })}
                    data-testid={`service-${i}-key`}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-400">
                    Icon-Name (z.B. Wifi)
                  </div>
                  <Input
                    value={s.icon || ""}
                    onChange={(e) => updateArrayItem("services", i, { icon: e.target.value })}
                    data-testid={`service-${i}-icon`}
                    className="rounded-xl"
                  />
                </div>
                <label className="flex items-end gap-2 text-sm pb-2">
                  <input
                    type="checkbox"
                    checked={!!s.dark}
                    onChange={(e) => updateArrayItem("services", i, { dark: e.target.checked })}
                    data-testid={`service-${i}-dark`}
                  />
                  Dark Card (groß / Feature)
                </label>
              </div>

              <ImageUploader
                value={s.image_url || ""}
                onChange={(url) => updateArrayItem("services", i, { image_url: url })}
                label="Bild (optional)"
                testId={`service-${i}-img`}
              />

              <LocalizedInput
                label="Titel"
                value={s.title || EMPTY_LOC}
                onChange={(v) => updateArrayItem("services", i, { title: v })}
                testId={`service-${i}-title`}
                required
              />
              <LocalizedInput
                label="Beschreibung"
                value={s.desc || EMPTY_LOC}
                onChange={(v) => updateArrayItem("services", i, { desc: v })}
                multi
                testId={`service-${i}-desc`}
              />

              <div className="flex justify-end">
                <button
                  onClick={() => removeArrayItem("services", i)}
                  data-testid={`service-${i}-remove`}
                  className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Service entfernen
                </button>
              </div>
            </SectionCard>
          ))}
          <button
            onClick={() =>
              setDoc((d) => ({
                ...d,
                services: [
                  ...(d.services || []),
                  {
                    key: `service-${Date.now()}`,
                    icon: "Sparkles",
                    image_url: "",
                    dark: false,
                    title: { ...EMPTY_LOC },
                    desc: { ...EMPTY_LOC },
                  },
                ],
              }))
            }
            data-testid="service-add"
            className="inline-flex items-center gap-2 rounded-full border border-dashed border-neutral-300 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 w-full justify-center"
          >
            <Plus className="w-4 h-4" />
            Service hinzufügen
          </button>
        </TabsContent>

        {/* TESTIMONIALS */}
        <TabsContent value="testimonials" className="space-y-4">
          {(doc.testimonials || []).map((t, i) => (
            <SectionCard key={i} title={`Testimonial ${i + 1}`}>
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-neutral-400">Name</div>
                <Input
                  value={t.name || ""}
                  onChange={(e) => updateArrayItem("testimonials", i, { name: e.target.value })}
                  data-testid={`testimonial-${i}-name`}
                  className="rounded-xl"
                />
              </div>
              <LocalizedInput
                label="Rolle"
                value={t.role || EMPTY_LOC}
                onChange={(v) => updateArrayItem("testimonials", i, { role: v })}
                testId={`testimonial-${i}-role`}
              />
              <LocalizedInput
                label="Zitat"
                value={t.text || EMPTY_LOC}
                onChange={(v) => updateArrayItem("testimonials", i, { text: v })}
                multi
                rows={3}
                testId={`testimonial-${i}-text`}
                required
              />
              <div className="flex justify-end">
                <button
                  onClick={() => removeArrayItem("testimonials", i)}
                  data-testid={`testimonial-${i}-remove`}
                  className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Entfernen
                </button>
              </div>
            </SectionCard>
          ))}
          <button
            onClick={addTestimonial}
            data-testid="testimonial-add"
            className="inline-flex items-center gap-2 rounded-full border border-dashed border-neutral-300 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 w-full justify-center"
          >
            <Plus className="w-4 h-4" />
            Testimonial hinzufügen
          </button>
        </TabsContent>

        {/* CONTACT */}
        <TabsContent value="contact" className="space-y-6">
          <SectionCard title="Kontaktdaten">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="text-xs uppercase tracking-wider text-neutral-500">
                  WhatsApp Nummer (mit +)
                </div>
                <Input
                  data-testid="contact-whatsapp"
                  value={doc.contact?.whatsapp_number || ""}
                  onChange={(e) => updateField("contact.whatsapp_number", e.target.value)}
                  placeholder="+34600000000"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1">
                <div className="text-xs uppercase tracking-wider text-neutral-500">E-Mail</div>
                <Input
                  type="email"
                  data-testid="contact-email"
                  value={doc.contact?.email || ""}
                  onChange={(e) => updateField("contact.email", e.target.value)}
                  className="rounded-xl"
                />
              </div>
            </div>

            <LocalizedInput
              label="Standort-Text"
              value={doc.contact?.location || EMPTY_LOC}
              onChange={(v) => updateField("contact.location", v)}
              testId="contact-location"
            />
          </SectionCard>

          <SectionCard title="Footer">
            <LocalizedInput
              label="Tagline"
              value={doc.footer?.tagline || EMPTY_LOC}
              onChange={(v) => updateField("footer.tagline", v)}
              multi
              testId="footer-tagline"
            />
          </SectionCard>
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
};

export default AdminSite;
